import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { KizenTypography } from '__app/typography';
import { Button } from '@kizen/kds/Button';
import Input from '__components/Kizen/Input';
import { Gradient } from '__components/Kizen/ScrollFadeContainer';
import {
  StyledButtonsWrapper,
  StyledPageWrapper,
  StyledPaginationWrapper,
  StyledSearchInput,
  StyledTableScrollContainer,
  StyledTableToolbar,
  StyledTableWrapper,
} from 'pages/KnowledgeSettings/styles';
import BasicTable from '__components/Tables/Basic';
import { TRow } from '__components/Kizen/Table';
import TablePagination from 'components/Tables/Big/TablePagination';
import {
  DEFAULT_PAGINATION_SETTINGS,
  ONLINE_SOURCES_TABLE_ACTIONS,
} from 'pages/KnowledgeSettings/constants';
import { useColumns } from './columns';
import { onlineSourceSearchQuery } from 'query/knowledge';
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import type { OnlineSourceListItem } from '@kizen/api/knowledge';
import { usePageConfig } from 'pages/KnowledgeSettings/hooks/usePageConfig';
import { useCatalogPermission } from 'pages/KnowledgeSettings/hooks/useCatalogPermission';
import Loader from '__components/Kizen/Loader';
import { useToast } from '__components/ToastProvider';
import { getOriginalError } from '__services/AxiosService';
import { getErrorMessage } from '__hooks/useErrors';
import KnowledgeService from 'services/KnowledgeService';
import ConfirmDeletionModal from '__components/Modals/presets/ConfirmDeletion';
import { type OnlineSourceData, OnlineSourceModal } from './OnlineSourceModal';

export const CatalogOnlineSourcesList = () => {
  const { t } = useTranslation();
  const { catalogId } = useParams<{ catalogId: string }>();
  const [showToast, toastVariant] = useToast();

  const { canWrite } = useCatalogPermission(catalogId);

  const {
    paginationSettings: { page, pageSize, ordering, search },
    actualSearch,
    handleChangePage,
    handleChangePageSize,
    handleChangeSearch,
    headData,
    pageIsReady,
  } = usePageConfig({
    pageKey: 'knowledge_crawl_configs_list',
    defaultConfig: DEFAULT_PAGINATION_SETTINGS,
  });

  const queryClient = useQueryClient();
  const [onlineSourceModal, setOnlineSourceModal] = useState<{
    onlineSource: OnlineSourceData;
    isStartModal: boolean;
  } | null>(null);
  const [sourceToDelete, setSourceToDelete] =
    useState<OnlineSourceListItem | null>(null);

  const queryOptions = useMemo(
    () =>
      onlineSourceSearchQuery({
        params: {
          page,
          page_size: pageSize,
          ordering,
          search,
          catalog_id: catalogId,
        },
        placeholderData: keepPreviousData,
        staleTime: 0,
        enabled: pageIsReady,
        refetchOnWindowFocus: false,
      }),
    [pageSize, page, ordering, search, catalogId, pageIsReady]
  );

  const { data, isLoading, isFetching } = useQuery(queryOptions);
  const loading = isLoading || isFetching;

  const onRefresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: queryOptions.queryKey });
  }, [queryClient, queryOptions.queryKey]);

  const onSelectAction = useCallback(
    async (
      { value }: { value: keyof typeof ONLINE_SOURCES_TABLE_ACTIONS },
      source: OnlineSourceListItem
    ) => {
      if (value === ONLINE_SOURCES_TABLE_ACTIONS.delete) {
        setSourceToDelete(source);
      }
      if (
        value === ONLINE_SOURCES_TABLE_ACTIONS.deactivate ||
        value === ONLINE_SOURCES_TABLE_ACTIONS.activate
      ) {
        try {
          await KnowledgeService.setOnlineSourceActiveStatus(
            source.id,
            value === ONLINE_SOURCES_TABLE_ACTIONS.activate
          );
          showToast({
            message: t('Online Source {{ action }} successfully', {
              action:
                value === ONLINE_SOURCES_TABLE_ACTIONS.activate
                  ? t('activated')
                  : t('deactivated'),
            }),
            variant: toastVariant.SUCCESS,
          });
          onRefresh();
        } catch (error) {
          showToast({
            message:
              getErrorMessage(getOriginalError(error)) ||
              t('Failed to update Online Source'),
            variant: toastVariant.FAILURE,
          });
        }
      }
      if (
        value === ONLINE_SOURCES_TABLE_ACTIONS.settings ||
        value === ONLINE_SOURCES_TABLE_ACTIONS.start
      ) {
        setOnlineSourceModal({
          onlineSource: source as OnlineSourceData,
          isStartModal: value === ONLINE_SOURCES_TABLE_ACTIONS.start,
        });
      }
      if (value === ONLINE_SOURCES_TABLE_ACTIONS.cancel) {
        try {
          await KnowledgeService.cancelOnlineSourceExecution(
            source.id,
            source.latest_execution!.id
          );
          showToast({
            message: t('Online Source cancelled successfully'),
            variant: toastVariant.SUCCESS,
          });
          onRefresh();
        } catch (error) {
          showToast({
            message:
              getErrorMessage(getOriginalError(error)) ||
              t('Failed to cancel Online Source'),
            variant: toastVariant.FAILURE,
          });
        }
      }
    },
    [onRefresh, showToast, t, toastVariant]
  );

  const onDeleteConfirmed = useCallback(async () => {
    try {
      if (sourceToDelete?.id) {
        await KnowledgeService.deleteOnlineSource(sourceToDelete.id);
        onRefresh();
        showToast({
          message: t('Online Source deleted successfully'),
          variant: toastVariant.SUCCESS,
        });
        setSourceToDelete(null);
      }
    } catch (error) {
      showToast({
        message:
          getErrorMessage(getOriginalError(error)) ||
          t('Failed to delete Online Source'),
        variant: toastVariant.FAILURE,
      });
    }
  }, [sourceToDelete?.id, onRefresh, showToast, t, toastVariant]);

  const onAddOnlineSource = useCallback(
    () =>
      setOnlineSourceModal({
        onlineSource: {
          id: '',
          mode: 'overwrite',
          base_url: '',
          include_patterns: [],
          exclude_patterns: [],
          description: '',
          max_pages: 100,
          max_depth: 4,
          respect_robots_txt: true,
          automatically_activate: true,
          is_active: true,
          latest_execution: null,
          active_execution: null,
          combined_status: 'setup',
        },
        isStartModal: false,
      }),
    []
  );

  const columns = useColumns({ onSelectAction, canWrite });

  return (
    <Loader loading={!pageIsReady}>
      <StyledPageWrapper>
        <StyledTableToolbar>
          <StyledSearchInput
            pill
            type={Input.INPUT_TYPES.SEARCH}
            placeholder={t('Find Sources')}
            value={actualSearch}
            onChange={handleChangeSearch}
            data-qa-id="catalog-online-sources-search"
          />
          <KizenTypography as="h2" type="header">
            {t('{{count}} Online Knowledge {{instance}}', {
              count: data?.count || 0,
              instance: data?.count === 1 ? t('Source') : t('Sources'),
            })}
          </KizenTypography>
          <StyledButtonsWrapper>
            <Button
              leftIcon="action-refresh"
              variant="text"
              onClick={onRefresh}
              color={loading ? 'inherit' : 'secondary'}
              disabled={loading}
              data-qa-id="catalog-online-sources-refresh-button"
            />
            {canWrite ? (
              <Button
                onClick={onAddOnlineSource}
                data-qa-id="catalog-add-online-source-button"
              >
                {t('Add Online Source')}
              </Button>
            ) : null}
          </StyledButtonsWrapper>
        </StyledTableToolbar>
        <StyledTableWrapper>
          <StyledTableScrollContainer bottom top gradient={<Gradient />}>
            <BasicTable
              stickyHeader
              head={<TRow head columns={columns} data={headData} />}
              staleData={loading}
            >
              {data?.count ? null : <TRow />}
              {data?.results.map((source) => (
                <TRow key={source.id} columns={columns} data={source} />
              ))}
            </BasicTable>
          </StyledTableScrollContainer>
        </StyledTableWrapper>
        <StyledPaginationWrapper>
          <TablePagination
            page={page}
            perPage={pageSize}
            totalCount={data?.count}
            onChangePage={handleChangePage}
            onChangePerPage={handleChangePageSize}
          />
        </StyledPaginationWrapper>

        <ConfirmDeletionModal
          show={Boolean(sourceToDelete)}
          onConfirm={onDeleteConfirmed}
          onHide={() => setSourceToDelete(null)}
        >
          {t('This will permanently delete the online source.')}
        </ConfirmDeletionModal>

        {onlineSourceModal ? (
          <OnlineSourceModal
            onlineSource={onlineSourceModal.onlineSource}
            isStartModal={onlineSourceModal.isStartModal}
            onConfirm={() => {
              onRefresh();
              setOnlineSourceModal(null);
            }}
            onHide={() => setOnlineSourceModal(null)}
            catalogId={catalogId}
          />
        ) : null}
      </StyledPageWrapper>
    </Loader>
  );
};
